/**
 * useCopy() — typed wrapper over react-i18next.
 *
 * Resolution order:
 *   1. Active-locale catalog entry.
 *   2. en catalog entry (i18next fallback chain).
 *   3. The explicit `fallback` argument.
 *
 * TMMPDM-0044: a numeric `count` selects i18next's plural form (`key_one` / `key_other`), so
 * the values go to the existence check too — a plural key has no bare `key` entry.
 */
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import '@/i18n/i18n';

interface InterpolationValues {
  [k: string]: string | number;
}

export function useCopy(): {
  t: (key: string, fallback?: string, values?: InterpolationValues) => string;
} {
  const { t: translate, i18n } = useTranslation();
  // Stable across renders: the identity changes only with the i18n instance or the active language,
  // so `t` is safe in effect dependency lists (a fresh `t` each render made such effects re-run forever).
  const t = useCallback(
    (key: string, fallback?: string, values?: InterpolationValues) => {
      const exists = i18n.exists(key, values ?? {});
      if (exists) {
        const translated = translate(key, values ?? {});
        if (typeof translated === 'string' && translated.length > 0 && translated !== key) {
          return translated;
        }
      }
      return interpolate(fallback ?? key, values);
    },
    // react-i18next re-creates `translate` when the active language changes, so listing it is what
    // gives `t` a new identity on a language switch (`i18n` itself is one stable instance).
    [i18n, translate],
  );
  return useMemo(() => ({ t }), [t]);
}

function interpolate(template: string, values: InterpolationValues | undefined): string {
  if (!values) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) => {
    const v = values[name];
    return v === undefined ? `{{${name}}}` : String(v);
  });
}
